import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import Loader from '../../../../atoms/icons/loader';
import RangeSelector from '../../../../atoms/range-slider';
import { fetchStockId } from '../../utils/marketsUtils';
import EmptyState from '../empty-state';
import './stock-graph.m.css';
import { useSelector } from 'react-redux';
import get from 'lodash/get';

const GRAPH_TYPE_MAP = {
  '1D': 'INTRADAY',
  '1W': 'thismonth',
  '1M': 'month1',
  '3M': 'month3',
  '6M': 'month6',
  '1Y': 'month12',
  YTD: 'thisyear',
  '5Y': 'year5',
};

/** Normalize API response to EXISTING UI contract */
const normalizeStockGraphData = (apiData, dashboardData) => {
  if (!apiData) return {};

  return {
    graphDetails: {
      graph: apiData.lineData?.map(([ts, price]) => [ts, Number(price)]) || [],
    },

    yearLowHigh: {
      lowPrice: dashboardData.low52Week,
      highPrice: dashboardData.high52Week,
      currentPrice: dashboardData.lastPrice,
    },

    lowHigh: {
      lowPrice: dashboardData.low,
      highPrice: dashboardData.high,
      currentprice: dashboardData.lastPrice,
    },

    stockPercentage: {
      name: apiData.name,
      value: dashboardData.changePerc,
    },

    exchangePercentage: {
      name: apiData.exchangeName || 'SENSEX',
      value: apiData.exchangePercent,
    },
  };
};

const formatGraphTime = (timestamp, duration) => {
  const date = new Date(Number(timestamp));

  if (duration === '1D') {
    // Intraday → show time
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  if (duration === 'YTD' || duration === '1Y' || duration === '5Y') {
    // Long term → show month + year
    return date.toLocaleDateString('en-IN', {
      month: 'short',
      year: 'numeric',
    });
  }

  // Default
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const StockGraph = () => {
  const allTabs = ['1D', '1W', '1M', '3M', '6M', 'YTD', '1Y', '5Y'];
  const [selectedDuration, setSelectedDuration] = useState('1D');
  const [movementData, setMovementData] = useState({});
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const marketApiEndpoint = useSelector((state) =>
    get(state, ['qt', 'config', 'integrations', 'market-api', 'endpoint'], ''),
  );

  const graphDataArray = [];
  const graphLabelArray = [];

  const stockId = 'INFY' || fetchStockId(); // INFY

  /** Fetch based on duration */
  const fetchGraphData = async (duration) => {
    setLoading(true);
    try {
      let url = '';

      if (duration === '1D') {
        url = `${marketApiEndpoint}/stocks/getLineGraphStockId?stockId=${stockId}`;
      } else {
        const graphType = GRAPH_TYPE_MAP[duration];
        url = `${marketApiEndpoint}/stocks/getLineGraphLtStockId?stockId=${stockId}&graphType=${graphType}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      const dashboardDataRes = await fetch(
        `${marketApiEndpoint}/stocks/dashboardStockId?stockId=${stockId}`,
      );
      const dashboardData = await dashboardDataRes.json();

      setMovementData(normalizeStockGraphData(data, dashboardData));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (stockId) {
      fetchGraphData(selectedDuration);
    }
  }, [stockId, selectedDuration]);

  useEffect(() => {
    const timer = setTimeout(() => setInitialLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  /** Graph data mapping (UNCHANGED USAGE) */
  const StringArray = movementData?.graphDetails?.graph?.map(JSON.stringify) || [];
  const uniqueArray = Array.from(new Set(StringArray), JSON.parse);

  uniqueArray.forEach(([label, value]) => {
    graphLabelArray.push(label);
    graphDataArray.push(value);
  });

  const data = {
    labels: graphLabelArray,
    datasets: [
      {
        label: 'Closing Price',
        data: graphDataArray,
        fill: true,
        backgroundColor: '#14c588',
        borderColor: '#14c588',
      },
    ],
  };

  const options = {
    animation: false,
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const ts = tooltipItems[0].label;
            return formatGraphTime(ts, selectedDuration);
          },
          label: (tooltipItem) => `Closing Price: ${tooltipItem.formattedValue}`,
        },
      },
    },
    elements: {
      point: { radius: 0 },
    },
    scales: {
      x: { display: false },
      y: { display: false },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  if (initialLoading) {
    return <div styleName="initial-loading-container"></div>;
  }

  return (
    <div styleName="stock-graph-container">
      {/* Tabs */}
      <div styleName="filters-container">
        <div styleName="btn-container">
          {allTabs.map((tab) => (
            <button
              key={tab}
              styleName={`filter-btn ${selectedDuration === tab ? 'selected' : ''}`}
              onClick={() => setSelectedDuration(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div styleName="loader-container">
          <Loader color="#dadee1" />
        </div>
      ) : (
        <>
          {/* Graph */}
          <div styleName="graph-container">
            {graphDataArray.length <= 1 ? (
              <div styleName="no-data-container-ext">
                <div styleName="no-data-container-int">
                  <EmptyState subTextMessage="for this stock at the moment" />
                </div>
              </div>
            ) : (
              <Line data={data} options={options} />
            )}
          </div>

          {/* Stats (UNCHANGED) */}
          <div styleName="stats-container">
            <div styleName="range-container">
              <RangeSelector
                min={movementData.yearLowHigh?.lowPrice}
                max={movementData.yearLowHigh?.highPrice}
                currentValue={movementData.yearLowHigh?.currentPrice}
                label=""
                labelOne="52 Week Low"
                labelTwo="52 Week High"
              />
              <RangeSelector
                min={movementData.lowHigh?.lowPrice}
                max={movementData.lowHigh?.highPrice}
                currentValue={movementData.lowHigh?.currentprice}
                label=""
                labelOne="Low"
                labelTwo="High"
              />
            </div>

            {/* <div styleName="imp-stats-container">
              <div styleName="stat-box">
                <span
                  styleName={
                    movementData?.stockPercentage?.value?.toString().includes('-')
                      ? 'stat-detail-value-negative'
                      : 'stat-detail-value-positive'
                  }
                >
                  {movementData?.stockPercentage?.value
                    ? `${movementData.stockPercentage.value} %`
                    : '-'}
                </span>
                <p>{movementData?.stockPercentage?.name || '-'}</p>
              </div>

              <div styleName="stat-box">
                <span
                  styleName={
                    movementData?.exchangePercentage?.value?.toString().includes('-')
                      ? 'stat-detail-value-negative'
                      : 'stat-detail-value-positive'
                  }
                >
                  {movementData?.exchangePercentage?.value
                    ? `${movementData.exchangePercentage.value} %`
                    : '-'}
                </span>
                <p>{movementData?.exchangePercentage?.name || '-'}</p>
              </div>
            </div> */}
          </div>
        </>
      )}
    </div>
  );
};

export default StockGraph;
